import React, { useState, useEffect, createContext, useContext } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import {
  Card,
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Badge,
  Drawer,
  IconButton,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
  Tooltip,
  DialogActions,
  Zoom
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { FaCoins, FaShoppingCart, FaTimes } from 'react-icons/fa';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';

interface StyledProps {
  darkMode?: boolean;
}

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    background: darkMode 
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
      : 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    color: darkMode ? 'white' : '#1a1a2e',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
    padding: theme.spacing(1),
  },
}));

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  borderRadius: '12px',
  padding: '8px 20px',
  fontWeight: 'bold',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  background: darkMode 
    ? 'linear-gradient(45deg, #00d2ff, #3a7bd5)' 
    : 'linear-gradient(45deg, #2196F3, #21CBF3)',
  color: 'white',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 150, 255, 0.4)',
    background: darkMode 
      ? 'linear-gradient(45deg, #3a7bd5, #00d2ff)' 
      : 'linear-gradient(45deg, #21CBF3, #2196F3)',
  },
  '&.Mui-disabled': {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.3)',
  }
}));

// Styled komponensek
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  position: 'relative',
  borderRadius: '20px',
  border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  background: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
  backdropFilter: 'blur(5px)',
  color: darkMode ? '#ffffff' : '#1a1a2e',
  padding: theme.spacing(1),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': { 
    transform: 'translateY(-8px)', 
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
    borderColor: '#00d2ff'
  }
}));

const CoinBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    background: '#ffd700',
    color: '#000'
  }
}));

// ShopContext a shop állapot kezeléséhez
const ShopContext = createContext<any>(null);

const ShopProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<any[]>([]);
  const [tokens, setTokens] = useState<number>(0);
  const [cartOpen, setCartOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [allAvatars, setAllAvatars] = useState<any[]>([]);
  const [userAvatars, setUserAvatars] = useState<any[]>([]);
  // Rank állapotok
  const [allRanks, setAllRanks] = useState<any[]>([]);
  const [userRanks, setUserRanks] = useState<any[]>([]);
  // Globális Snackbar state-ek
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSuccess, setSnackbarSuccess] = useState<boolean>(false);
  // Backdrop állapot a rank vásárláshoz
  const [backdropOpen, setBackdropOpen] = useState(false);
 

  const refreshUserRanks = async () => {
    try {
      const token = Cookies.get('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getranks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Feltételezzük, hogy az API egy tömböt ad vissza (pl.: ["AdamantineParagon"])
        setUserRanks(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error refreshing user ranks", err);
    }
  };

  

  // Token lekérése
  useEffect(() => {
    async function fetchTokens() {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getusertoken`, {
          headers: { Authorization: `Bearer ${Cookies.get('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTokens(data.token);
        } else {
          setError('Failed to fetch tokens');
        }
      } catch (err) {
        setError('Error fetching tokens');
      }
    }
    fetchTokens();
  }, []);

  // Avatarok lekérése (admin API: URL, user API: price, rank)
  useEffect(() => {
    async function fetchAvatars() {
      try {
        const token = Cookies.get('token');
        const resAll = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getallavatars`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const resUser = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getavatars`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resAll.ok && resUser.ok) {
          const dataAll = await resAll.json();
          const dataUser = await resUser.json();
          setAllAvatars(Array.isArray(dataAll) ? dataAll : []);
          setUserAvatars(Array.isArray(dataUser.avatars) ? dataUser.avatars : []);
        } else {
          setError('Failed to fetch avatars');
        }
      } catch (err) {
        setError('Error fetching avatars');
      }
    }
    fetchAvatars();
  }, []);

  // Rankok lekérése
  useEffect(() => {
    async function fetchRanks() {
      try {
        const token = Cookies.get('token');
        const resAllRanks = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getallranks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const resUserRanks = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getranks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resAllRanks.ok && resUserRanks.ok) {
          const dataAllRanks = await resAllRanks.json();
          const dataUserRanks = await resUserRanks.json();
          setAllRanks(Array.isArray(dataAllRanks) ? dataAllRanks : []);
          setUserRanks(Array.isArray(dataUserRanks) ? dataUserRanks : []);
        } else {
          setError('Failed to fetch ranks');
        }
      } catch (err) {
        setError('Error fetching ranks');
      }
    }
    fetchRanks();
  }, []);

  // Új segédfüggvény: Felhasználó avatarjainak frissítése
  const refreshUserAvatars = async () => {
    try {
      const token = Cookies.get('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getavatars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserAvatars(Array.isArray(data.avatars) ? data.avatars : []);
      }
    } catch (err) {
      console.error('Error refreshing avatars', err);
    }
  };

  // addToCart: Duplikáció és token ellenőrzés
  const addToCart = (avatar: any): boolean => {
    if (userAvatars.includes(avatar.url)) {
      setError('Avatar already owned!');
      return false;
    }
    if (cart.some((item) => item.url === avatar.url)) {
      setError('This avatar is already in your cart!');
      return false;
    }
    if (tokens >= avatar.price) {
      setCart([...cart, avatar]);
      setTokens(tokens - avatar.price);
      return true;
    } else {
      setError('Not enough tokens! Transfer your scores or buy tokens.');
      return false;
    }
  };

  // Avatar eltávolítása a kosárból
  const removeFromCart = (avatarUrl: string) => {
    const avatar = cart.find((item) => item.url === avatarUrl);
    if (avatar) {
      setCart(cart.filter((item) => item.url !== avatarUrl));
      setTokens(tokens + avatar.price);
    }
  };

  // Checkout
  const checkout = async () => {
    try {
      let successStatus = true;
      for (const avatar of cart) {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/buyavatars`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${Cookies.get('token')}`
          },
          body: JSON.stringify({
            token: avatar.price,
            avatar: avatar.url
          })
        });
        if (!res.ok) {
          successStatus = false;
          setSnackbarMessage('Purchase failed for an item.');
          setSnackbarSuccess(false);
          setOpenSnackbar(true);
          break;
        }
      }
      if (successStatus) {
        setSnackbarMessage('Purchase successful! Check your account!');
        setSnackbarSuccess(true);
        setOpenSnackbar(true);
        const updated = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getavatars`, {
          headers: { Authorization: `Bearer ${Cookies.get('token')}` }
        });
        if (updated.ok) {
          const dataUser = await updated.json();
          setUserAvatars(Array.isArray(dataUser.avatars) ? dataUser.avatars : []);
          setCart([]);
          setCartOpen(false);
        }
      }
    } catch (err) {
      setSnackbarMessage('Error during checkout!');
      setSnackbarSuccess(false);
      setOpenSnackbar(true);
    }
  };

  // Rank vásárlás (megerősítő dialoggal)
    const buyRank = async (rank: any) => {
      if (tokens < rank.price) {
        setError('Not enough tokens!');
        return;
      }
      setBackdropOpen(true);
      setTimeout(async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/buyrank`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${Cookies.get('token')}`
            },
            body: JSON.stringify({
              token: rank.price,
              rank: rank.name
            })
          });
          if (res.ok) {
            const data = await res.json();
            // Frissítjük a cookiet a role és roles mezőkkel, ha elérhetők
            if (data.role) {
              Cookies.set('role', data.role);
            }
            if (data.roles) {
              Cookies.set('roles', JSON.stringify(data.roles));
            }
            setSnackbarMessage('Rank purchase successful!');
            setSnackbarSuccess(true);
            setTokens((prev) => prev - rank.price);
            await refreshUserRanks();
          } else {
            setSnackbarMessage('Rank purchase failed!');
            setSnackbarSuccess(false);
          }
        } catch (err) {
          setSnackbarMessage('Error during rank purchase!');
          setSnackbarSuccess(false);
        }
        setOpenSnackbar(true);
        setBackdropOpen(false);
      }, 3000);
    };


  return (
    <ShopContext.Provider
      value={{
        cart,
        tokens,
        addToCart,
        removeFromCart,
        cartOpen,
        setCartOpen,
        error,
        setError,
        allAvatars,
        userAvatars,
        checkout,
        openSnackbar,
        setOpenSnackbar,
        snackbarMessage,
        setSnackbarMessage,
        snackbarSuccess,
        setSnackbarSuccess,
        allRanks,
        userRanks,
        buyRank,
        refreshUserAvatars,
        backdropOpen
      }}
    >
      {children}
      <Backdrop open={backdropOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </ShopContext.Provider>
  );
};

// AvatarCard – megjeleníti az avatart; ha a felhasználó nem jogosult (locked), a card sötétebb, és egy Lock ikon látható.
// Rákattintáskor egy Details Dialog nyílik meg, amelyben az avatar adatai szerepelnek.
// Részlet az AvatarCard komponensből (After)
// FRONTEND – AvatarCard komponens (After)
const AvatarCard = ({ avatar }: { avatar: any }) => {
  const { addToCart, userAvatars, cart, userRanks } = useContext(ShopContext);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [ranksData, setRanksData] = useState<any[]>([]);
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  // Lekérjük a /getallranks API-t, hogy megkapjuk a rankok nevét és leírását
  useEffect(() => {
    const fetchRanksData = async () => {
      try {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getallranks`, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        // Feltételezzük, hogy data egy tömb, pl.:
        // [ { "price": 10.00, "name": "CopperApprentice", "description": "Small description for the CopperApprentice" }, ... ]
        setRanksData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching ranks data:', error);
      }
    };
    fetchRanksData();
  }, []);

  // Ha az avatar rendelkezik rank értékkel, keressük meg a hozzá tartozó rank adatot a ranksData tömbből.
  const rankInfo = avatar.rank ? ranksData.find(r => r.name === avatar.rank) : null;
  const displayName = rankInfo ? rankInfo.name : (avatar.name || `Ordinary Avatar`);
  const detailedDescription = rankInfo ? rankInfo.description : (avatar.description || `Ordinary Avatar is an immersive experience that allows players to step into the shoes of an everyday individual navigating a world filled with extraordinary possibilities.`);

  const owned = userAvatars.includes(avatar.url);
  const inCart = cart.some((item: any) => item.url === avatar.url);
  const currentRank = userRanks.length > 0 ? userRanks[0] : null;
  const isEnabled = (!avatar.rank) || (currentRank && avatar.rank === currentRank);
  const isLocked = !isEnabled;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEnabled) {
      setSnackbarMessage('This avatar is not available for purchase with your current rank!');
      setOpenSnackbar(true);
      return;
    }
    if (owned) {
      setSnackbarMessage('You already own this avatar!');
      setOpenSnackbar(true);
      return;
    }
    const success = addToCart(avatar);
    if (success) {
      setSnackbarMessage('Avatar successfully added to cart!');
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <StyledCard
  darkMode={darkMode}
  onClick={() => setOpenDetails(true)}
  sx={{ 
    // Gradiens alkalmazása ha elérhető (függetlenül attól, hogy megvette-e már)
    background: isLocked 
      ? (darkMode ? '#2e2e2e' : '#dcdcdc') 
      : (darkMode 
          ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
          : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)'),
    
    // Vizualizáció: ha már megvette, adjunk neki egy belső ragyogást vagy keretet
    boxShadow: owned 
      ? (darkMode ? 'inset 0 0 20px rgba(255,0,0,0.3)' : 'inset 0 0 20px rgba(33,203,243,0.3)')
      : 'none',
    
    border: owned 
      ? `2px solid ${darkMode ? '#ff4d4d' : '#21cbf3'}` 
      : `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,

    // Ha meg van véve, egy kicsit "halványabb" vagy "tisztább" lehet a tartalom
    opacity: isLocked ? 0.6 : 1,
    transition: 'all 0.3s ease'
  }}
>
        <Box component="img" src={avatar.url} alt="Avatar" sx={{ width: '100%', height: 120, objectFit: 'cover' }} />
        {isLocked && (
          <Box sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#fff',
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            padding: '4px'
          }}>
            <LockIcon />
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Typography variant="h6">{displayName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FaCoins style={{ color: '#ffd700' }} />
              <Typography sx={{ ml: 1 }}>{avatar.price}</Typography>
            </Box>
            {owned ? (
              <Button variant="contained" disabled sx={{ backgroundColor: '#555555', '&.Mui-disabled': { color: '#fff' } }} onClick={(e) => e.stopPropagation()}>
                Owned
              </Button>
            ) : inCart ? (
              <Button variant="contained" disabled sx={{ '&.Mui-disabled': { color: '#fff' } }} onClick={(e) => e.stopPropagation()}>
                Already in your cart
              </Button>
            ) : (
              <ActionButton darkMode={darkMode} onClick={handleAddToCart} disabled={!isEnabled}>
                  Add to Cart
              </ActionButton>
            )}
          </Box>
        </Box>

        

        <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}>
          <Alert severity="success" onClose={() => setOpenSnackbar(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </StyledCard>

      <AnimatedDialog open={openDetails} onClose={() => setOpenDetails(false)} TransitionComponent={Zoom} darkMode={darkMode} disableScrollLock>
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>Avatar Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <Avatar 
              src={avatar.url} 
              sx={{ 
                width: 150, 
                height: 150, 
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                border: '4px solid #00d2ff' 
              }} 
            />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{displayName}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FaCoins style={{ color: '#ffd700', fontSize: '1.4rem' }} />
              <Typography variant="h6" color="#ffd700">{avatar.price}</Typography>
            </Box>
            <Typography variant="body1" sx={{ textAlign: 'center', opacity: 0.8 }}>
              {detailedDescription}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <ActionButton darkMode={darkMode} onClick={() => setOpenDetails(false)} sx={{ minWidth: 120 }}>
            Close
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>



      
    </>
  );
};





// RankCard – a Rank vásárlási lehetőséghez, megerősítő dialoggal
const RankCard = ({ rank }: { rank: any }) => {
  const { tokens, userRanks, buyRank } = useContext(ShopContext);
  const [localSnackbarOpen, setLocalSnackbarOpen] = useState(false);
  const [localSnackbarMessage, setLocalSnackbarMessage] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const owned = userRanks.includes(rank.name);

  const handleBuyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (owned) {
      setLocalSnackbarMessage('You already own this rank!');
      setLocalSnackbarOpen(true);
      return;
    }
    if (tokens < rank.price) {
      setLocalSnackbarMessage('Not enough tokens!');
      setLocalSnackbarOpen(true);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmBuy = () => {
    setConfirmDialogOpen(false);
    buyRank(rank);
  };

  return (
    <>
     <StyledCard 
        darkMode={isDarkMode}
        onClick={() => setOpenDetails(true)}
        sx={{ 
          // Ugyanaz a gradiens, mint az Avatarnál
          background: isDarkMode 
            ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
            : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
          
          // Megkülönböztetés: ha birtokolja a rangot, vastagabb/színesebb keret
          border: owned 
            ? `3px solid ${isDarkMode ? '#ff0000' : '#25379c'}` 
            : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
          
          // Kiemelés animációval, ha birtokolja
          boxShadow: owned 
            ? `0 0 15px ${isDarkMode ? 'rgba(94, 11, 11, 0.8)' : 'rgba(37, 55, 156, 0.5)'}` 
            : 'none',

          color: 'white', // A sötét/kék gradiensen a fehér szöveg olvashatóbb
          transition: 'all 0.3s ease'
        }}
      >
        <Box sx={{ mt: 1 }}>
          <Typography variant="h6">{rank.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FaCoins style={{ color: '#ffd700' }} />
              <Typography sx={{ ml: 1 }}>{rank.price}</Typography>
            </Box>
            {owned ? (
              <Button variant="contained" disabled sx={{ backgroundColor: '#555555' }}>
                Owned
              </Button>
            ) : (
              <Button variant="contained" color="secondary" onClick={handleBuyClick}>
                Buy
              </Button>
            )}
          </Box>
        </Box>
        <Snackbar open={localSnackbarOpen} autoHideDuration={3000} onClose={() => setLocalSnackbarOpen(false)}>
          <Alert severity="info" onClose={() => setLocalSnackbarOpen(false)}>
            {localSnackbarMessage}
          </Alert>
        </Snackbar>
      </StyledCard>

      {/* Details Dialog */}
      <AnimatedDialog 
        open={openDetails} 
        onClose={() => setOpenDetails(false)}
        TransitionComponent={Zoom} // Nyitási/zárási animáció
        darkMode={isDarkMode}
        disableScrollLock
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem' }}>
          {rank.name} Details
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Ár rész - Kiemelt stílusban */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: 'rgba(255, 215, 0, 0.1)',
              p: 2,
              borderRadius: '15px',
              border: '1px solid rgba(255, 215, 0, 0.3)'
            }}>
              <FaCoins style={{ color: '#ffd700', marginRight: 12, fontSize: '1.8rem' }}/>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ffd700' }}>
                {rank.price} Tokens
              </Typography>
            </Box>

            {/* Rövid leírás */}
            <Typography variant="subtitle1" sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              p: 2,
              borderRadius: '12px',
              fontWeight: 'medium',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              {rank.description}
            </Typography>

            {/* Részletes rank leírás formázással */}
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.6,
                fontSize: '0.95rem',
                p: 2,
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                fontFamily: 'system-ui, sans-serif',
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              {rank.rankDescription}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <ActionButton 
            darkMode={true} 
            onClick={() => setOpenDetails(false)}
            sx={{ px: 4 }}
          >
            Close
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>

      <AnimatedDialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)}
        darkMode={isDarkMode}
        TransitionComponent={Zoom}
        transitionDuration={400}
        maxWidth="xs"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem' }}>
          Confirm Purchase
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', color: isDarkMode ? 'rgba(255,255,255,0.9)' : '#1a1a2e' }}>
            Are you sure you want to buy the <strong style={{ color: isDarkMode ? '#00d2ff' : '#2196F3' }}>{rank.name}</strong> rank?
          </Typography>
          
          {/* Látványos Ár Kiemelés */}
          <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: 'rgba(255, 215, 0, 0.1)',
            p: 2,
            borderRadius: '15px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            mb: 1
          }}>
            <FaCoins style={{ color: '#ffd700', marginRight: 10, fontSize: '1.8rem' }}/>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ffd700' }}>
              {rank.price} Tokens
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button 
            onClick={() => setConfirmDialogOpen(false)} 
            sx={{ 
              color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', 
              fontWeight: 'bold',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <ActionButton darkMode={isDarkMode} onClick={handleConfirmBuy}>
            Yes, I buy
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>
    </>
  );
};

const Cart = () => {
  const { cart, cartOpen, setCartOpen, removeFromCart, checkout } = useContext(ShopContext);
  const darkMode = true;

  return (
    <Drawer 
      anchor="right" 
      open={cartOpen} 
      onClose={() => setCartOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 380 },
          background: darkMode ? 'rgba(26, 26, 46, 0.95)' : '#fff',
          backdropFilter: 'blur(15px)',
          color: darkMode ? 'white' : 'inherit',
          borderLeft: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'divider'}`
        }
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Shopping Cart</Typography>
          <IconButton onClick={() => setCartOpen(false)} sx={{ color: darkMode ? 'white' : 'inherit' }}>
            <FaTimes />
          </IconButton>
        </Box>
        
        {cart.length === 0 ? (
          <Typography sx={{ textAlign: 'center', opacity: 0.5, mt: 4 }}>Your cart is empty</Typography>
        ) : (
          cart.map((item) => (
            <Card key={item.url} sx={{ 
              mb: 2, p: 1.5, 
              background: 'rgba(255,255,255,0.05)', 
              borderRadius: '15px',
              display: 'flex', 
              alignItems: 'center' 
            }}>
              <Avatar src={item.url} variant="rounded" sx={{ width: 60, height: 60, mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{item.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                   <FaCoins style={{ color: '#ffd700', fontSize: '0.9rem', marginRight: '4px' }} />
                   <Typography variant="body2">{item.price}</Typography>
                </Box>
              </Box>
              <IconButton onClick={() => removeFromCart(item.url)} color="error">
                <FaTimes size={18} />
              </IconButton>
            </Card>
          ))
        )}

        {cart.length > 0 && (
          <ActionButton 
            darkMode={darkMode} 
            fullWidth 
            sx={{ mt: 4, py: 2, fontSize: '1.1rem' }} 
            onClick={checkout}
          >
            Checkout Now
          </ActionButton>
        )}
      </Box>
    </Drawer>
  );
};

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

// AvatarShop komponens – megjeleníti az összes avatar vagy rank cardot a szűrés alapján
const AvatarShop = () => {
  const navigate = useNavigate();
  const {
    cart,
    tokens,
    setCartOpen,
    error,
    setError,
    allAvatars,
    allRanks,
    openSnackbar,
    setOpenSnackbar,
    snackbarMessage,
    snackbarSuccess
  } = useContext(ShopContext);
  const [sortBy, setSortBy] = useState<string>('PriceLowHigh');
  const [filterType, setFilterType] = useState<string>('Avatar');
  const [isAdmin, setIsAdmin] = useState(false);
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  let displayItems: any[] = [];
  if (filterType === 'Rank') {
    displayItems = allRanks;
  } else if (filterType === 'All') {
    displayItems = allAvatars;
  } else {
    displayItems = allAvatars.filter((item) => (item.type || 'Avatar') === filterType);
  }

  const sortedItems = [...displayItems].sort((a, b) =>
    sortBy === 'PriceLowHigh' ? a.price - b.price : b.price - a.price
  );

  useEffect(() => {
    const role = Cookies.get("role");
    const roles = Cookies.get("roles");
    if (role === "ADMIN" || roles === 'ADMIN') {
      setIsAdmin(true);
    }
  }, []);

  return (
    <Container sx={{ pt: 8, mt: '64px' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          {/* A Select komponenseket egy közös stílus objektummal is elláthatjuk a redundancia elkerülése végett */}
          {[
            { value: sortBy, onChange: (e: any) => setSortBy(e.target.value), options: [
              { v: "PriceLowHigh", t: "Sort by Price Low-High" },
              { v: "PriceHighLow", t: "Sort by Price High-Low" }
            ]},
            { value: filterType, onChange: (e: any) => setFilterType(e.target.value), options: [
              { v: "Avatar", t: "Avatar" },
              { v: "Rank", t: "Rank 💎" }
            ]}
          ].map((select, idx) => (
            <Select
              key={idx}
              value={select.value}
              onChange={select.onChange}
              size="small"
              // Kényszerítjük, hogy a Dialog/Card rétegén belül maradjon
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: {
                    // DINAMIKUS GRADIENS: Pontosan az AvatarCard logikáját követve
                    background: darkMode 
                      ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
                      : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
                    color: 'white', // Mindkét gradiensen a fehér szöveg mutat jól
                    borderRadius: '12px',
                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.4)'}`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    mt: 0.5,
                    // Az elemek (MenuItem) stílusa a listán belül
                    '& .MuiMenuItem-root': {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                      },
                      '&.Mui-selected': {
                        background: 'rgba(255, 255, 255, 0.25)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.35)',
                        }
                      }
                    }
                  }
                }
              }}
              sx={{
                minWidth: 150,
                color: darkMode ? 'white' : '#1a1a2e',
                borderRadius: '10px',
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#00d2ff',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? 'white' : '#1a1a2e',
                }
              }}
            >
              {select.options.map(opt => (
                <MenuItem key={opt.v} value={opt.v}>{opt.t}</MenuItem>
              ))}
            </Select>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ActionButton 
            darkMode={true} 
            onClick={() => navigate('/myscore')}
          >
            Transfer scores to tokens
          </ActionButton>

          <Tooltip title={!isAdmin ? "This feature is currently not available." : ""} arrow>
            <span> 
              <ActionButton
                darkMode={true}
                onClick={() => navigate("/click")}
                disabled={!isAdmin}
                sx={{
                  // Ha admin, akkor zöldes gradiens, ha nem, marad az alap
                  background: isAdmin ? 'linear-gradient(45deg, #2ecc71, #27ae60)' : undefined
                }}
              >
                Get Tokens
              </ActionButton>
            </span>
          </Tooltip>

          {/* Token kijelző */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            background: 'rgba(0,0,0,0.3)', 
            padding: '6px 16px', 
            borderRadius: '20px',
            border: '1px solid rgba(255, 215, 0, 0.3)'
          }}>
            <CoinBadge badgeContent={tokens}>
              <FaCoins size={20} style={{ color: '#ffd700' }} />
            </CoinBadge>
            <Typography variant="h6" sx={{ color: '#ffd700', fontWeight: 'bold' }}>
              {tokens.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        {filterType !== 'Rank' && (
          <IconButton
            onClick={() => setCartOpen(true)}
            sx={{
              background: darkMode ? 'rgba(26, 26, 46, 0.95)' : 'rgba(24, 154, 197, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '12px',
              transition: 'all 0.3s ease',
              animation: cart.length > 0 ? `${pulseAnimation} 1.5s infinite` : 'none',
              '&:hover': {
                background: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(24, 154, 197, 0.88)',
                transform: 'scale(1.1)',
              },
            }}
          >
            <Badge 
              badgeContent={cart.length} 
              color="error"
              sx={{ 
                '& .MuiBadge-badge': { 
                  fontWeight: 'bold',
                  fontSize: '0.8rem'
                } 
              }}
            >
              <FaShoppingCart size={22} style={{ color: '#00d2ff' }} />
            </Badge>
          </IconButton>
        )}
      </Box>
      <Grid container spacing={3}>
        {sortedItems.map((item: any, index: number) => (
          <Grid item xs={12} sm={6} md={4} key={item.url || item.name || index}>
            {filterType === 'Rank' ? <RankCard rank={item} /> : <AvatarCard avatar={item} />}
          </Grid>
        ))}
      </Grid>
      {filterType !== 'Rank' && <Cart />}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity={snackbarSuccess ? 'success' : 'error'} onClose={() => setOpenSnackbar(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Fő Shop komponens
const Shop: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isAuthenticated = !!Cookies.get('token');

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ShopProvider>
      <div style={{ position: 'relative', minHeight: '100vh' }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1
          }}
        >
          <Particles />
        </div>
        {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
        <AvatarShop />
      </div>
    </ShopProvider>
  );
};

export default Shop;
